import './App.css'
import MMLRendererMultiplayer from './components/MMLRendererMultiplayer'
import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { LanguageProvider } from './i18n/LanguageContext'
import HeaderControls from './components/HeaderControls'
import { useLanguage } from './i18n/LanguageContext'
import ChatSocketIO from './components/ChatSocketIO'
import { MultiplayerManagerRef } from './components/MultiplayerManager'
import { Socket, io } from 'socket.io-client'
// Importar o componente de áudio do Daily.co
import AudioControls from './components/daily/AudioControlsOptimized'
import { Link } from 'react-router-dom'
import LaserEyesProvider from './components/wallet/LaserEyesProvider'
import WalletConnectButton from './components/wallet/WalletConnectButtonNew'
import SocialIcons from './components/SocialIcons'

// Inicializar Socket.io globalmente para garantir que esteja disponível desde o início
const initializeGlobalSocket = () => {
  const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3000';
  console.log('Inicializando Socket.io global:', serverUrl);

  // Configurações otimizadas para o Render Free Tier
  // Usar apenas polling para maior estabilidade
  const socket = io(serverUrl, {
    transports: ['polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 60000, // Aumentado para 60 segundos
    autoConnect: true,
    forceNew: false, // Permitir reutilização de conexões
    withCredentials: false,
    // Configurar pingTimeout e pingInterval para evitar desconexões
    pingTimeout: 60000, // 60 segundos
    pingInterval: 25000, // 25 segundos
    // Adicionar path explícito para evitar problemas de roteamento
    path: '/socket.io/',
    // Configurações adicionais para melhorar a estabilidade
    upgrade: false, // Desativar upgrade para websocket para evitar problemas
    rememberUpgrade: false,
    secure: window.location.protocol === 'https:',
    rejectUnauthorized: false
  });

  // Expor o socket globalmente para debug e para ser usado por outros componentes
  (window as any)._globalSocket = socket;

  // Criar um elemento de status de conexão oculto para uso interno
  const statusElement = document.createElement('div');
  statusElement.id = 'connection-status';
  statusElement.style.position = 'fixed';
  statusElement.style.bottom = '10px';
  statusElement.style.left = '10px';
  statusElement.style.display = 'none'; // Ocultar o elemento
  statusElement.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
  statusElement.style.color = '#FFF';
  statusElement.style.padding = '5px 10px';
  statusElement.style.borderRadius = '5px';
  statusElement.style.fontSize = '12px';
  statusElement.style.zIndex = '9999';
  statusElement.textContent = 'Conectando...';
  document.body.appendChild(statusElement);

  // Configurar listeners básicos
  socket.on('connect', () => {
    console.log('Socket.io global conectado:', socket.id);
    statusElement.textContent = 'Conectado';
    statusElement.style.color = '#4CAF50';

    // Registrar o jogador imediatamente após a conexão
    // Isso garante que o jogador seja registrado mesmo após reconexões
    setTimeout(() => {
      if (socket.connected) {
        console.log('Enviando evento player:join após conexão...');
        const persistentId = localStorage.getItem('multiplayer_persistent_id') || `user-${Math.random().toString(36).substring(2, 10)}`;
        localStorage.setItem('multiplayer_persistent_id', persistentId);

        // Posição inicial padrão
        const initialPosition = { x: -14.5, y: 14.2, z: 315.0 };
        const initialRotation = { x: 0, y: 0, z: 0 };

        socket.emit('player:join', {
          position: initialPosition,
          rotation: initialRotation,
          persistentId: persistentId
        });
      }
    }, 500);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket.io global desconectado:', reason);
    statusElement.textContent = 'Desconectado';
    statusElement.style.color = '#F44336';

    // Reconectar automaticamente com backoff exponencial
    const reconnectDelay = Math.floor(Math.random() * 1000) + 1000; // 1-2 segundos
    console.log(`Tentando reconectar em ${reconnectDelay}ms...`);
    setTimeout(() => {
      if (!socket.connected) {
        try {
          statusElement.textContent = 'Reconectando...';
          statusElement.style.color = '#FFC107';
          socket.connect();
        } catch (error) {
          console.error('Erro ao tentar reconectar:', error);
          statusElement.textContent = 'Erro de reconexão';
          statusElement.style.color = '#F44336';
        }
      }
    }, reconnectDelay);
  });

  // Adicionar listeners para eventos de conexão
  socket.on('connect_error', (error) => {
    console.error('Socket.io global connect_error:', error);
    statusElement.textContent = 'Erro de conexão';
    statusElement.style.color = '#F44336';
  });

  socket.on('error', (error) => {
    console.error('Socket.io global error:', error);
    statusElement.textContent = 'Erro';
    statusElement.style.color = '#F44336';
  });

  // Adicionar ping periódico para manter a conexão ativa
  setInterval(() => {
    if (socket.connected) {
      socket.emit('ping', { timestamp: Date.now() });
    }
  }, 20000); // A cada 20 segundos

  return socket;
};

// Inicializar o socket global imediatamente
const globalSocket = initializeGlobalSocket();

// Test data array for bitmap visualization - 282 transactions (indices 0-281)
const testData = [5,6,6,5,6,5,7,5,5,6,6,8,5,5,5,5,6,5,4,6,5,5,5,5,5,6,5,5,5,5,5,7,5,7,5,5,5,5,5,5,5,5,5,4,5,5,5,5,5,5,5,4,5,5,4,5,5,5,5,4,4,5,4,6,4,5,4,5,5,4,4,4,5,5,6,5,5,4,4,6,5,4,5,5,5,4,5,4,5,5,4,4,4,4,4,3,4,3,3,3,5,5,4,3,3,3,2,3,4,3,5,4,3,3,3,4,3,4,5,2,4,5,5,5,5,5,4,5,4,4,4,4,3,4,3,5,5,4,5,4,4,3,5,4,4,5,5,5,5,3,4,5,5,3,4,4,5,2,3,5,4,5,5,5,5,4,4,3,4,4,3,4,4,3,3,3,4,3,4,3,4,4,4,4,4,3,3,4,3,4,4,4,4,4,4,4,4,3,4,4,4,4,4,3,3,3,4,4,4,4,4,3,3,3,3,4,3,3,4,3,3,4,3,4,1,3,3,3,4,2,4,3,4,5,2,3,4,3,3,3,4,3,3,4,3,2,2,2,3,3,2,2,3,3,2,2,2,4,3,3,3,1,3,2,1,1,1,1,1,4,2,3,1,2,1,2,4,2,2,8,2,2];

import { safeStorage, setupStorageErrorHandler } from './utils/safeStorage';

// App content component that uses translations
const AppContent = () => {
  const { t } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<string | null>(null);
  const [currentParcel, setCurrentParcel] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasMultiversoPass, setHasMultiversoPass] = useState(false);
  // Removendo a opção de alternar para o Mondrian Layout, agora usamos apenas MML
  const [mmlContent, setMmlContent] = useState<string>('');
  // Referência para o componente MultiplayerManager para acessar o socket
  const multiplayerRef = useRef<MultiplayerManagerRef>(null);
  // Referência para o socket global
  const socketRef = useRef<Socket | null>(globalSocket);

  // Inicializa o tratamento de erros para localStorage e outros problemas comuns
  useLayoutEffect(() => {
    // Configura o tratamento de erros de acesso ao storage
    setupStorageErrorHandler();
  }, []);

  // Handle search input change - now only updates the input value
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  }, []);

  // Handle search form submission
  const handleSearchSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parcelNumber = parseInt(searchValue.trim());
    if (!isNaN(parcelNumber) && parcelNumber >= 0) {
      // Formatar o ID da parcela no formato esperado: "parcel-X-size-Y"
      // Como não sabemos o tamanho, vamos apenas procurar por "parcel-X"
      const parcelIdPrefix = `parcel-${parcelNumber}`;
      console.log('Searching for parcel with prefix:', parcelIdPrefix);

      // Definir o ID da parcela para que o MMLRendererMultiplayer possa encontrá-la
      setSelectedParcel(parcelIdPrefix);

      // Clear the search input after successful search
      setSearchValue('');
    } else {
      console.log('Invalid parcel number:', searchValue);
      // Opcional: mostrar uma mensagem de erro para o usuário
    }
  }, [searchValue]);

  // Toggle chat panel
  const toggleChat = useCallback(() => {
    setIsChatOpen(prev => !prev);
  }, []);

  // Callback para atualizar o status do Multiverso Pass
  const handleMultiversoPassUpdate = useCallback((hasAccess: boolean) => {
    setHasMultiversoPass(hasAccess);
    console.log('Multiverso Pass status atualizado:', hasAccess ? 'Possui Pass' : 'Sem Pass');

    // Atualizar o status no MultiplayerManager para informar outros jogadores
    if (multiplayerRef.current) {
      multiplayerRef.current.updateMultiversoPassStatus(hasAccess);
      console.log('Enviado status de Multiverso Pass para outros jogadores via MultiplayerManager:', hasAccess);
    } else if (socketRef.current) {
      // Fallback para o socket direto se o MultiplayerManager não estiver disponível
      socketRef.current.emit('player:update', { hasMultiversoPass: hasAccess });
      console.log('Enviado status de Multiverso Pass para outros jogadores via socket direto:', hasAccess);
    }
  }, []);

  // Removido o toggle entre MML e Mondrian

  // Load MML content
  useEffect(() => {
    const loadMML = async () => {
      try {
        // Load MML from the public folder
        const response = await fetch('/mml/203779_4x.xml'); // Usando o mapa redimensionado 4x
        if (!response.ok) {
          throw new Error(`Failed to load MML file: ${response.statusText}`);
        }
        const content = await response.text();
        setMmlContent(content);
      } catch (error) {
        console.error('Error loading MML:', error);
      }
    };

    loadMML();
  }, []);

  return (
    <div className="App" style={{ height: '100vh', overflow: 'hidden' }}>
      {/* Main content */}
      <div style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#000'
      }}>
        {/* Header controls */}
        <HeaderControls />

        {/* Container para Wallet Connect Button e Social Icons */}
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '20px'
        }}>
          {/* Social Icons */}
          <SocialIcons />

          {/* Wallet Connect Button */}
          <WalletConnectButton
            style={{
              marginLeft: '5px'
            }}
            onAccessChange={handleMultiversoPassUpdate}
          />
        </div>

        {/* Current parcel text - more subtle styling */}
        {currentParcel !== null && (
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255, 255, 255, 0.6)', // More transparent text
            fontSize: '14px', // Slightly smaller
            padding: '6px 12px', // Smaller padding
            borderRadius: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.4)', // More transparent background
            border: '1px solid rgba(138, 43, 226, 0.15)', // More subtle border
            backdropFilter: 'blur(5px)',
            zIndex: 3,
            whiteSpace: 'nowrap',
            letterSpacing: '0.5px', // Better readability
            fontWeight: '300', // Lighter font weight
            transition: 'opacity 0.3s ease', // Smooth transition
            opacity: 0.8
          }}>
            {`${t('parcel')} ${currentParcel ? currentParcel.replace('parcel-', '').split('-')[0] : ''}`}
          </div>
        )}

        {/* Botão de alternância removido - agora usamos apenas MML */}

        {/* Search form overlay */}
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 2
        }}>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="number"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={t('searchPlaceholder')}
              min="0"
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid rgba(138, 43, 226, 0.15)',
                width: '180px',
                fontSize: '14px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                color: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(5px)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
            />
            <button
              type="submit"
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid rgba(138, 43, 226, 0.15)',
                backgroundColor: 'rgba(138, 43, 226, 0.4)',
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '14px',
                cursor: 'pointer',
                backdropFilter: 'blur(5px)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease'
              }}
            >
              {t('searchButton')}
            </button>
          </form>
        </div>

        {/* Full screen 3D renderer - apenas MML */}
        <MMLRendererMultiplayer
          ref={multiplayerRef}
          mmlContent={mmlContent}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1
          }}
          selectedParcel={selectedParcel}
          onParcelSelect={setSelectedParcel}
          onCurrentParcel={setCurrentParcel}
          hasMultiversoPass={hasMultiversoPass}
        />

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(138, 43, 226, 0.2)',
          backdropFilter: 'blur(5px)',
          zIndex: 3,
          letterSpacing: '0.5px',
          fontFamily: 'monospace'
        }}>
          <span>203779.bitmap - Multiverso</span>
        </div>

        {/* Daily.co Audio Controls */}
        <AudioControls
          roomUrl="https://multiverso.daily.co/audio-global"
          isChatOpen={isChatOpen}
        />

        {/* Chat toggle button */}
        <button
          onClick={toggleChat}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: isChatOpen ? '340px' : '20px',
            zIndex: 1000,
            padding: '12px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(138, 43, 226, 0.3)',
            color: 'rgba(138, 43, 226, 0.9)',
            cursor: 'pointer',
            width: '50px',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            transition: 'all 0.3s ease'
          }}
        >
          {isChatOpen ? '✕' : '💬'}
        </button>

        {/* Floating chat panel */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '300px',
          height: '400px',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          transform: `translateX(${isChatOpen ? '0' : 'calc(100% + 20px)'})`,
          transition: 'transform 0.3s ease',
          zIndex: 999,
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          {/* Usar o socket global para o chat para garantir persistência */}
          {console.log('App: socketRef:', socketRef.current)}
          {socketRef.current ? (
            <ChatSocketIO socket={socketRef.current} />
          ) : (
            <div style={{ padding: '20px', color: 'white', textAlign: 'center' }}>
              Conectando ao servidor...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main App component with LanguageProvider and LaserEyesProvider
function App() {
  return (
    <LanguageProvider>
      <LaserEyesProvider>
        <AppContent />
      </LaserEyesProvider>
    </LanguageProvider>
  );
}

export default App;
